import { BadgeVariant } from "../../../../../components/Badge";
import { SelectSheetOption } from "../../../../../components/MobileForm/types";
import {
  ActivityFilterType,
  ActivityOrder,
  CorporateActionType,
  HoldingActionState,
  TradeCommand,
  TradeCommandCorrectionReason,
  TradeCommandType,
} from "../../../../../services/holdings";
import { ChargeNoun } from "../../../types";
import { ActivityColumn, ActivityDatePreset, ActivityTypeTone } from "./types";

// One source for header, widths, and body alignment, mirroring HOLDING_COLUMNS; percentages sum to 100 under table-layout: fixed since nothing here needs a pixel-exact track.
export const ACTIVITY_COLUMNS: ActivityColumn[] = [
  { key: "date", label: "Date", width: "12%" },
  { key: "type", label: "Type", width: "14%" },
  { key: "holding", label: "Holding", width: "12%" },
  { key: "details", label: "Details", width: "32%" },
  { key: "quantity", label: "Quantity", width: "10%", align: "end" },
  { key: "amount", label: "Amount", width: "14%", align: "end" },
  { key: "actions", label: "Actions", width: "6%", labelHidden: true },
];

// Same two charges the panels label, but worded for mid-copy sentences ("the 4,20 € fee recorded with it") where a capitalised form would read as a proper noun.
export const CHARGE_SENTENCE_NOUNS: Record<ChargeNoun, string> = {
  fee: "fee",
  withholdingTax: "tax",
};

// Read by header and row body alike — a figure misaligned from its heading reads as belonging to the neighbouring column.
export const activityColumnAlign = (
  key: ActivityColumn["key"],
): ActivityColumn["align"] =>
  ACTIVITY_COLUMNS.find((column) => column.key === key)?.align;

// Total over the vocabulary since PMA-302 Part 2: the endpoint emits account-level types (DEPOSIT, WITHDRAWAL, ACCOUNT_FEE, INTEREST, ADJUSTMENT) alongside security-linked ones, and a missing key would render `undefined`.
export const TRADE_TYPE_LABELS: Record<TradeCommandType, string> = {
  BUY_SETTLEMENT: "Buy",
  SELL_SETTLEMENT: "Sell",
  DIVIDEND: "Dividend",
  WITHHOLDING_TAX: "Withholding tax",
  FEE: "Fee",
  CASH_IN_LIEU: "Cash in lieu",
  DEPOSIT: "Deposit",
  WITHDRAWAL: "Withdrawal",
  ACCOUNT_FEE: "Account fee",
  INTEREST: "Interest",
  ADJUSTMENT: "Adjustment",
  INTEREST_TAX: "Interest tax",
  CASHBACK: "Cashback",
};

// Charges read as warnings so the rows that cost money stand out from the trades around them.
export const TRADE_TYPE_TONES: Record<TradeCommandType, ActivityTypeTone> = {
  BUY_SETTLEMENT: "neutral",
  SELL_SETTLEMENT: "neutral",
  DIVIDEND: "neutral",
  WITHHOLDING_TAX: "warning",
  FEE: "warning",
  CASH_IN_LIEU: "neutral",
  DEPOSIT: "neutral",
  WITHDRAWAL: "neutral",
  ACCOUNT_FEE: "warning",
  INTEREST: "neutral",
  ADJUSTMENT: "neutral",
  INTEREST_TAX: "warning",
  CASHBACK: "neutral",
};

// Buy and sell only fall back here when the price is missing; interest is keyed on direction in the builder.
export const TRADE_DETAIL_SENTENCES: Record<TradeCommandType, string> = {
  BUY_SETTLEMENT: "Units bought",
  SELL_SETTLEMENT: "Units sold",
  DIVIDEND: "Cash dividend",
  WITHHOLDING_TAX: "Tax withheld",
  FEE: "Broker fee",
  CASH_IN_LIEU: "Fractional entitlement settled in cash",
  DEPOSIT: "Cash deposited",
  WITHDRAWAL: "Cash withdrawn",
  ACCOUNT_FEE: "Charged to account cash",
  INTEREST: "Interest earned on cash",
  ADJUSTMENT: "Balance adjustment",
  INTEREST_TAX: "Tax withheld on interest",
  CASHBACK: "Cashback credited to account cash",
};

export const INTEREST_DETAIL_SENTENCES: Record<
  TradeCommand["moneyDirection"],
  string
> = {
  IN: "Interest earned on cash",
  OUT: "Interest charged on cash",
};

// Shorter than ACCOUNT_LEVEL_POSITION, which the sheet and delete dialog still use as a sentence subject.
export const ACCOUNT_HOLDING_LABEL = "Account";

export const LOCKED_FALLBACK_REASON = "This entry can't be corrected here.";

export const CORPORATE_ACTION_LOCK_REASON =
  "Corporate actions are settled by reconciling the position, not edited here.";

// One map, not two: split apart, a pill reading INCOME could end up next to a neutral- or expense-tinted amount. Keyed on moneyDirection, not type, since e.g. interest is charged as often as earned.
export const MONEY_DIRECTION_DISPLAY: Record<
  TradeCommand["moneyDirection"],
  { pill: string; amount: "income" | "expense" }
> = {
  IN: { pill: "INCOME", amount: "income" },
  OUT: { pill: "EXPENSE", amount: "expense" },
};

// Keyed on the reason, not on kebab presence, because the reason says who owns the correction; the Record over the union means an API-added reason can't ship without user-facing copy. CORRECTABLE is null — nothing to explain.
export const CORRECTION_REASON_NOTES: Record<
  TradeCommandCorrectionReason,
  string | null
> = {
  CORRECTABLE: null,
  CORPORATE_ACTION_OWNED:
    "This settlement belongs to a corporate action, so it is corrected by reconciling that action rather than by editing the trade.",
  // Still transaction-owned even though a transfer can be recorded through the ledger page: both endpoints must be corrected together there.
  TRANSFER_OWNED:
    "This movement belongs to a transfer, so it is corrected by editing that transaction rather than the brokerage entry.",
  SHAPE_UNSUPPORTED:
    "This entry has an unsupported combination of movements, so it can only be deleted and recorded again.",
  ACCOUNT_LEVEL_UNSUPPORTED:
    "This isn't a simple account fee or interest charge, so it can't be edited or deleted here.",
  MULTI_HOLDING_UNSUPPORTED:
    "This entry spans more than one position, so it can't be edited or deleted here.",
};

/** The em dash the table prints where a type carries no value for a column. */
export const EMPTY_CELL = "—";

// Not ACCOUNT_LEVEL_POSITION: an action always targets a position (holdingId isn't nullable), so a missing symbol is a lookup gap, not a claim the action touched cash.
export const UNKNOWN_POSITION = "Unknown position";

// A Record over the union, like TRADE_TYPE_LABELS, so an API-added type is a compile error here instead of an `undefined` in the feed.
export const CORPORATE_ACTION_TYPE_LABELS: Record<CorporateActionType, string> =
  {
    split: "Split",
    symbol_change: "Symbol change",
    merger: "Merger",
    spin_off: "Spin-off",
    delisting: "Delisting",
    reverse_split_cash_in_lieu: "Reverse split",
  };

// Mirrors the Holdings badge's wording (services/valuation.ts) so a blocked position reads "Needs correction" in both places. The action's own `status` is deliberately not shown — another holder's reconciliation settles nothing for this position.
export const ACTION_STATE_LABELS: Record<HoldingActionState, string> = {
  APPLIED: "Applied",
  RECONCILED: "Reconciled",
  RECONCILABLE: "Needs correction",
  BLOCKED: "Needs correction",
};

// Distinguishes the two states sharing the "Needs correction" label: RECONCILABLE is the owner's to record, BLOCKED can't be corrected in PMA at all. RECONCILED is null — its second line is the settlement date, which only the entry carries.
export const ACTION_STATE_NOTES: Record<HoldingActionState, string | null> = {
  APPLIED: null,
  RECONCILED: null,
  RECONCILABLE: "Record what this position became before it can be valued.",
  BLOCKED:
    "PMA can't apply this action automatically, so this position can't be valued yet.",
};

// Same severity reading as VALUATION_BADGE_VARIANTS (applied is fact, awaiting the owner is a warning, unactionable is an error); typed as BadgeVariant so the ledger and this feed draw from one palette instead of two that can drift.
export const ACTION_STATE_VARIANTS: Record<HoldingActionState, BadgeVariant> = {
  APPLIED: "default",
  RECONCILED: "success",
  RECONCILABLE: "warning",
  BLOCKED: "error",
};

// Plural filter nouns, not the row labels; the API keeps TRANSFER-origin cash out of DEPOSIT/WITHDRAWAL, hence "Imported".
// No interest-tax or cashback option: no write path records either without its interest or buy, so the filter could only ever come back empty.
export const ACTIVITY_TYPE_FILTER_OPTIONS: Record<
  Exclude<ActivityFilterType, "INTEREST_TAX" | "CASHBACK">,
  string
> = {
  BUY_SETTLEMENT: "Buys",
  SELL_SETTLEMENT: "Sells",
  DIVIDEND: "Dividends",
  WITHHOLDING_TAX: "Withholding tax",
  FEE: "Fees",
  CASH_IN_LIEU: "Cash in lieu",
  TRANSFER: "Transfers",
  DEPOSIT: "Imported cash in",
  WITHDRAWAL: "Imported cash out",
  ACCOUNT_FEE: "Account fees",
  INTEREST: "Interest",
  ADJUSTMENT: "Adjustments",
  CORPORATE_ACTION: "Corporate actions",
};

// Fixed so the checked set serialises in one order, whichever box was ticked first.
export const ACTIVITY_TYPE_FILTER_ORDER = Object.keys(
  ACTIVITY_TYPE_FILTER_OPTIONS,
) as (keyof typeof ACTIVITY_TYPE_FILTER_OPTIONS)[];

export const ACTIVITY_DATE_OPTIONS: {
  value: ActivityDatePreset;
  label: string;
}[] = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "currentYear", label: "Current year" },
  { value: "year", label: "Specific year" },
  { value: "allTime", label: "All time" },
];

// Direction only: amount or quantity would sort one page, not the feed, and disagree with the next page.
export const ACTIVITY_ORDER_OPTIONS: SelectSheetOption[] = [
  { value: "DESC", label: "Newest first" },
  { value: "ASC", label: "Oldest first" },
];

export const ACTIVITY_ORDER_PHRASES: Record<ActivityOrder, string> = {
  DESC: "newest first",
  ASC: "oldest first",
};

// Mirror of the API's MAX_ACTIVITY_HOLDING_FILTER_IDS — kept independent since the packages build separately.
export const MAX_ACTIVITY_HOLDING_FILTER_IDS = 100;

export const ACCOUNT_LEVEL_VALUE = "account";
export const ACCOUNT_LEVEL_LABEL = "Account-wide entries";

export const EMPTY_RANGE_TITLE = "No activity in this date range";
export const EMPTY_RANGE_BODY =
  "Choose a wider date range to see earlier entries.";

export const NO_MATCH_TITLE = "No activity matches";
export const NO_MATCH_BODY = "Adjust the holdings or transaction-type filters.";

export const NEW_ACCOUNT_TITLE = "No account activity yet";
export const NEW_ACCOUNT_BODY =
  "Trades, transfers and income will appear after the first entry.";
